export type TourPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface Box { top: number; left: number; width: number; height: number }
export interface Size { width: number; height: number }
export interface PopoverPosition { top: number; left: number; placement: TourPlacement | 'center' }

const EDGE = 12; // keep the card this far from the viewport edges
const GAP = 14; // space between the highlighted element and the card

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, Math.max(min, max)));

/** Smallest box that covers every given box (a step may highlight several elements, e.g. Prev and Next). */
export function unionBox(boxes: Box[]): Box | null {
  if (!boxes.length) return null;
  const top = Math.min(...boxes.map((b) => b.top));
  const left = Math.min(...boxes.map((b) => b.left));
  const bottom = Math.max(...boxes.map((b) => b.top + b.height));
  const right = Math.max(...boxes.map((b) => b.left + b.width));
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * Where to put the step card: beside the highlighted box on the first side it fits,
 * starting with the preferred side. With no box, or no side that fits, the card is centered
 * (or pinned to the bottom when the highlighted box fills the screen).
 */
export function placePopover(target: Box | null, card: Size, viewport: Size, preferred?: TourPlacement): PopoverPosition {
  const centered = {
    top: clamp((viewport.height - card.height) / 2, EDGE, viewport.height - card.height - EDGE),
    left: clamp((viewport.width - card.width) / 2, EDGE, viewport.width - card.width - EDGE),
  };
  if (!target) return { ...centered, placement: 'center' };

  const midX = target.left + target.width / 2 - card.width / 2;
  const midY = target.top + target.height / 2 - card.height / 2;
  const maxLeft = viewport.width - card.width - EDGE;
  const maxTop = viewport.height - card.height - EDGE;

  const options: Record<TourPlacement, () => { top: number; left: number; fits: boolean }> = {
    bottom: () => {
      const top = target.top + target.height + GAP;
      return { top, left: clamp(midX, EDGE, maxLeft), fits: top + card.height <= viewport.height - EDGE };
    },
    top: () => {
      const top = target.top - GAP - card.height;
      return { top, left: clamp(midX, EDGE, maxLeft), fits: top >= EDGE };
    },
    right: () => {
      const left = target.left + target.width + GAP;
      return { top: clamp(midY, EDGE, maxTop), left, fits: left + card.width <= viewport.width - EDGE };
    },
    left: () => {
      const left = target.left - GAP - card.width;
      return { top: clamp(midY, EDGE, maxTop), left, fits: left >= EDGE };
    },
  };

  const order: TourPlacement[] = ['bottom', 'top', 'right', 'left'];
  if (preferred) order.unshift(...order.splice(order.indexOf(preferred), 1));
  for (const side of order) {
    const spot = options[side]();
    if (spot.fits) return { top: spot.top, left: spot.left, placement: side };
  }
  // Nothing fits beside it (a tall section on a short screen): overlap its lower part instead.
  return { top: Math.max(EDGE, maxTop), left: centered.left, placement: 'center' };
}
